import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ShieldCheck, Receipt, TrendingUp, FileText, RefreshCw, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { printHtml, PRINT_BLOCKED_MESSAGE } from '../lib/printDocument';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BIRReportsPage() {
  const { token, currentOutlet } = useAuth();
  const outletId = currentOutlet?.id;

  const [activeTab, setActiveTab] = useState('summary');
  const [grandTotal, setGrandTotal] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [eisQueue, setEisQueue] = useState([]);
  const [xReading, setXReading] = useState(null);
  const [zReadings, setZReadings] = useState([]);
  const [adjustmentDocs, setAdjustmentDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    if (!outletId || !token) return;

    setLoading(true);
    try {
      const [grandRes, receiptRes, eisRes, zReadRes, adjRes] = await Promise.all([
        fetch(`${API_URL}/api/bir/grand-total?outlet_id=${outletId}`, { headers: authHeaders }),
        fetch(`${API_URL}/api/bir/receipts?outlet_id=${outletId}&limit=100`, { headers: authHeaders }),
        fetch(`${API_URL}/api/bir/eis-queue?outlet_id=${outletId}&limit=100`, { headers: authHeaders }),
        fetch(`${API_URL}/api/cashier/z-readings?outlet_id=${outletId}&limit=30`, { headers: authHeaders }),
        fetch(`${API_URL}/api/bir/adjustment-documents?outlet_id=${outletId}&limit=100`, { headers: authHeaders }),
      ]);

      if (grandRes.ok) setGrandTotal(await grandRes.json());

      if (receiptRes.ok) {
        const data = await receiptRes.json();
        setReceipts(data.receipts || []);
      }

      if (eisRes.ok) {
        const data = await eisRes.json();
        setEisQueue(data.items || []);
      }

      if (zReadRes.ok) {
        const data = await zReadRes.json();
        setZReadings(data.z_readings || []);
      }

      if (adjRes.ok) {
        const data = await adjRes.json();
        setAdjustmentDocs(data.documents || []);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load BIR reports');
    }
    setLoading(false);
  }, [outletId, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const generateXRead = async () => {
    if (!outletId) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bir/x-reading?outlet_id=${outletId}`, {
        method: 'POST',
        headers: authHeaders,
      });

      if (!res.ok) throw new Error('Failed to generate X-Read');

      const data = await res.json();
      setXReading(data);
      toast.success(`X-Read generated: ${data.x_number}`);
    } catch (err) {
      toast.error('Failed to generate X-Read');
    }
    setLoading(false);
  };

  const printAdjustmentDoc = (doc) => {
    const html = `
      <html>
        <head>
          <title>${doc.document_number}</title>
          <style>
            body { font-family: monospace; font-size: 12px; padding: 20px; }
            .center { text-align: center; }
            .line { border-top: 1px dashed #000; margin: 8px 0; }
            .row { display: flex; justify-content: space-between; gap: 12px; }
            .bold { font-weight: bold; }
            .small { font-size: 10px; word-break: break-all; }
          </style>
        </head>
        <body>
          <div class="center bold">${doc.document_type} SLIP</div>
          <div class="center">Fiscal Adjustment Document</div>
          <div class="line"></div>

          <div class="row"><span>Document No.</span><span>${doc.document_number || ''}</span></div>
          <div class="row"><span>Original OR</span><span>${doc.original_receipt_number || ''}</span></div>
          <div class="row"><span>Amount</span><span>${money(doc.amount)}</span></div>
          <div class="row"><span>Created At</span><span>${doc.created_at || ''}</span></div>
          <div class="row"><span>Created By</span><span>${doc.created_by || ''}</span></div>
          <div class="row"><span>Approved By</span><span>${doc.approved_by || ''}</span></div>

          <div class="line"></div>
          <div class="bold">Reason</div>
          <div>${doc.reason || ''}</div>

          <div class="line"></div>
          <div class="bold">HASH</div>
          <div class="small">${doc.hash || ''}</div>

          <div class="line"></div>
          <div class="center">REFERENCE DOCUMENT ONLY</div>
          <div class="center">Tied to Original OR: ${doc.original_receipt_number || ''}</div>
        </body>
      </html>
    `;

    if (!printHtml(html)) toast.error(PRINT_BLOCKED_MESSAGE);
  };

  const printZRead = (z) => {
    const totals = z.totals || {};
    const html = `
      <html>
        <head>
          <title>Z-Read ${z.z_number}</title>
          <style>
            body { font-family: monospace; font-size: 12px; padding: 20px; }
            .center { text-align: center; }
            .line { border-top: 1px dashed #000; margin: 8px 0; }
            .row { display: flex; justify-content: space-between; gap: 12px; }
            .bold { font-weight: bold; }
            .small { font-size: 10px; word-break: break-all; }
          </style>
        </head>
        <body>
          <div class="center bold">OFFICIAL Z-READING</div>
          <div class="center">End of Business Day Report</div>
          <div class="line"></div>

          <div class="row"><span>Z No.</span><span>${z.z_number || ''}</span></div>
          <div class="row"><span>Business Date</span><span>${z.business_date || ''}</span></div>
          <div class="row"><span>Locked At</span><span>${z.locked_at || ''}</span></div>
          <div class="row"><span>Locked By</span><span>${z.locked_by || ''}</span></div>

          <div class="line"></div>
          <div class="row bold"><span>Gross Sales</span><span>${money(totals.gross_sales)}</span></div>
          <div class="row"><span>Net Sales</span><span>${money(totals.net_sales)}</span></div>
          <div class="row"><span>VAT Collected</span><span>${money(totals.total_tax)}</span></div>
          <div class="row"><span>Total Discounts</span><span>${money(totals.total_discounts)}</span></div>
          <div class="row"><span>Service Charge</span><span>${money(totals.total_service_charge ?? totals.service_charge)}</span></div>
          <div class="row"><span>Voids</span><span>${money(totals.total_voids)}</span></div>
          <div class="row"><span>Refunds</span><span>${money(totals.total_refunds)}</span></div>

          <div class="line"></div>
          <div class="row"><span>Cash Sales</span><span>${money(totals.cash_sales)}</span></div>
          <div class="row"><span>Card Sales</span><span>${money(totals.card_sales)}</span></div>
          <div class="row"><span>QR Sales</span><span>${money(totals.qr_sales)}</span></div>
          ${[['Bank Transfer', totals.bank_sales], ['Store Credit', totals.store_credit_sales],
             ['Online', totals.online_sales], ['Other', totals.other_sales]]
            .filter(([, v]) => Number(v || 0) > 0)
            .map(([label, v]) => `<div class="row"><span>${label}</span><span>${money(v)}</span></div>`)
            .join('')}
          ${totals.total_tendered != null
            ? `<div class="row"><span><b>Total Tendered</b></span><span><b>${money(totals.total_tendered)}</b></span></div>`
            : ''}
          <div class="row"><span>Transaction Count</span><span>${totals.transaction_count || 0}</span></div>

          <div class="line"></div>
          <div class="row"><span>Starting Cash</span><span>${money(totals.starting_cash)}</span></div>
          <div class="row"><span>Cash In</span><span>${money(totals.cash_in)}</span></div>
          <div class="row"><span>Cash Out</span><span>${money(totals.cash_out)}</span></div>
          <div class="row"><span>Expected Cash</span><span>${money(totals.expected_cash)}</span></div>
          <div class="row"><span>Counted Cash</span><span>${money(totals.counted_cash)}</span></div>
          <div class="row"><span>Variance</span><span>${money(totals.variance)}</span></div>

          <div class="line"></div>
          <div class="bold">HASH</div>
          <div class="small">${z.hash || ''}</div>

          <div class="line"></div>
          <div class="center">THIS Z-READ LOCKS THE BUSINESS DATE</div>
          <div class="center">No additional sales may be posted to this date.</div>
        </body>
      </html>
    `;

    if (!printHtml(html)) toast.error(PRINT_BLOCKED_MESSAGE);
  };

  const logReceiptReprint = async (receipt) => {
    const reason = window.prompt(
      `Enter required reason for reprinting receipt ${receipt.receipt_number}:`
    );

    if (!reason || !reason.trim()) {
      toast.error('Reprint reason is required');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bir/receipts/${receipt.id}/reprint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason: reason.trim() })
      });

      if (!res.ok) throw new Error('Failed to log receipt reprint');

      toast.success(`Reprint logged for ${receipt.receipt_number}`);
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to log receipt reprint');
    }
    setLoading(false);
  };

  if (!outletId) {
    return (
      <div className="p-6 text-foreground">
        Select an outlet first.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-6" data-testid="bir-reports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-red-400" />
            BIR Reports
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            X-Read, grand total accumulator, issued receipts, and EIS queue.
          </p>
        </div>

        <Button onClick={loadData} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="xread">X-Read</TabsTrigger>
          <TabsTrigger value="zread">Z-Read</TabsTrigger>
          <TabsTrigger value="adjustments">Adjustment Register</TabsTrigger>
          <TabsTrigger value="receipts">Receipt Journal</TabsTrigger>
          <TabsTrigger value="eis">EIS Queue</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SummaryCard icon={TrendingUp} label="Grand Gross Sales" value={money(grandTotal?.grand_gross_sales)} />
            <SummaryCard icon={TrendingUp} label="Grand Net Sales" value={money(grandTotal?.grand_net_sales)} />
            <SummaryCard icon={TrendingUp} label="Grand VAT" value={money(grandTotal?.grand_vat)} />
            <SummaryCard icon={Receipt} label="Grand Voids" value={money(grandTotal?.grand_voids)} />
            <SummaryCard icon={Receipt} label="Grand Refunds" value={money(grandTotal?.grand_refunds)} />
            <SummaryCard icon={FileText} label="Z-Reading Count" value={grandTotal?.z_reading_count ?? 0} />
            <SummaryCard icon={FileText} label="Reset Counter" value={grandTotal?.reset_counter ?? 0} />
          </div>
        </TabsContent>

        <TabsContent value="xread" className="mt-6">
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">Generate X-Read</h2>
                <p className="text-sm text-muted-foreground">
                  X-Read is inquiry-only. It does not lock or reset the business day.
                </p>
              </div>
              <Button onClick={generateXRead} disabled={loading} className="bg-red-600 hover:bg-red-700">
                Generate X-Read
              </Button>
            </div>

            {xReading && (
              <pre className="bg-black/40 rounded p-4 overflow-auto text-xs">
                {JSON.stringify(xReading, null, 2)}
              </pre>
            )}
          </div>
        </TabsContent>

        <TabsContent value="zread" className="mt-6">
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Z No.</th>
                  <th className="text-left p-3">Business Date</th>
                  <th className="text-right p-3">Gross</th>
                  <th className="text-right p-3">Net</th>
                  <th className="text-right p-3">VAT</th>
                  <th className="text-left p-3">Locked At</th>
                  <th className="text-left p-3">Hash</th>
                  <th className="text-right p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {zReadings.map(z => {
                  const totals = z.totals || {};
                  return (
                    <tr key={z.id} className="border-t border-border">
                      <td className="p-3 font-semibold">{z.z_number}</td>
                      <td className="p-3">{z.business_date}</td>
                      <td className="p-3 text-right">{money(totals.gross_sales)}</td>
                      <td className="p-3 text-right">{money(totals.net_sales)}</td>
                      <td className="p-3 text-right">{money(totals.total_tax)}</td>
                      <td className="p-3">{z.locked_at}</td>
                      <td className="p-3 text-xs break-all max-w-xs">{z.hash}</td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => printZRead(z)}>
                          Print Z
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {zReadings.length === 0 && (
                  <tr>
                    <td colSpan="8" className="p-6 text-center text-muted-foreground">No Z-Readings yet.</td>
                  </tr>
                )}
              </tbody>
            </table></div>
          </div>
        </TabsContent>

        <TabsContent value="adjustments" className="mt-6">
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Document No.</th>
                  <th className="text-left p-3">Original OR</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-left p-3">Reason</th>
                  <th className="text-left p-3">Created At</th>
                  <th className="text-left p-3">Hash</th>
                  <th className="text-right p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {adjustmentDocs.map(doc => (
                  <tr key={doc.id} className="border-t border-border">
                    <td className="p-3 font-semibold">{doc.document_type}</td>
                    <td className="p-3">{doc.document_number}</td>
                    <td className="p-3">{doc.original_receipt_number}</td>
                    <td className="p-3 text-right">{money(doc.amount)}</td>
                    <td className="p-3 max-w-xs truncate" title={doc.reason}>{doc.reason}</td>
                    <td className="p-3">{doc.created_at}</td>
                    <td className="p-3 text-xs break-all max-w-xs">{doc.hash}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => printAdjustmentDoc(doc)}>
                        Print
                      </Button>
                    </td>
                  </tr>
                ))}

                {adjustmentDocs.length === 0 && (
                  <tr>
                    <td colSpan="8" className="p-6 text-center text-muted-foreground">
                      No fiscal adjustment documents yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table></div>
          </div>
        </TabsContent>

        <TabsContent value="receipts" className="mt-6">
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Receipt No.</th>
                  <th className="text-left p-3">Business Date</th>
                  <th className="text-right p-3">Total</th>
                  <th className="text-right p-3">VAT</th>
                  <th className="text-right p-3">VAT Exempt</th>
                  <th className="text-right p-3">Discount</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Hash</th>
                  <th className="text-right p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {receipts.map(r => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="p-3 font-semibold">{r.receipt_number}</td>
                    <td className="p-3">{r.business_date}</td>
                    <td className="p-3 text-right">{money(r.total_amount)}</td>
                    <td className="p-3 text-right">{money(r.vat_amount)}</td>
                    <td className="p-3 text-right">{money(r.vat_exempt_sales)}</td>
                    <td className="p-3 text-right">{money(r.discount_amount)}</td>
                    <td className="p-3 uppercase">{r.status}</td>
                    <td className="p-3 text-xs break-all max-w-xs">{r.hash}</td>
                    <td className="p-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={loading}
                        onClick={() => logReceiptReprint(r)}
                      >
                        Log Reprint
                      </Button>
                    </td>
                  </tr>
                ))}
                {receipts.length === 0 && (
                  <tr>
                    <td colSpan="9" className="p-6 text-center text-muted-foreground">No issued receipts yet.</td>
                  </tr>
                )}
              </tbody>
            </table></div>
          </div>
        </TabsContent>

        <TabsContent value="eis" className="mt-6">
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Queue ID</th>
                  <th className="text-left p-3">Receipt Record</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Attempts</th>
                  <th className="text-left p-3">Created</th>
                  <th className="text-left p-3">Last Error</th>
                </tr>
              </thead>
              <tbody>
                {eisQueue.map(x => (
                  <tr key={x.id} className="border-t border-border">
                    <td className="p-3 text-xs">{x.id}</td>
                    <td className="p-3 text-xs">{x.receipt_record_id || '-'}</td>
                    <td className="p-3 uppercase flex items-center gap-2">
                      <Send className="w-3 h-3" />
                      {x.status}
                    </td>
                    <td className="p-3 text-right">{x.attempts}</td>
                    <td className="p-3">{x.created_at}</td>
                    <td className="p-3 text-red-300">{x.last_error || '-'}</td>
                  </tr>
                ))}
                {eisQueue.length === 0 && (
                  <tr>
                    <td colSpan="6" className="p-6 text-center text-muted-foreground">No EIS queue items yet.</td>
                  </tr>
                )}
              </tbody>
            </table></div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-5 h-5 text-teal-400" />
        <span className="text-muted-foreground text-sm">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

function money(value) {
  const n = Number(value || 0);
  return `₱${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
